import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  DocumentAccessResponse,
  DocumentListResponse,
  DocumentMimeType,
  DocumentView,
  UploadAuthorizationRequest,
  UploadAuthorizationResponse,
} from '@tex/finance-shared'
import { apiGet, apiSend } from './api'
import { lenderKeys } from './lenders'

const IDEMPOTENCY_HEADER = 'Idempotency-Key'

export const documentKeys = {
  list: (caseId: string) => ['cases', 'detail', caseId, 'documents'] as const,
}

/** Mirrors the API allow-list (apps/api/src/services/document-types.ts). */
export const DOCUMENT_MIME_TYPES: readonly DocumentMimeType[] = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024

const BY_EXTENSION: Record<string, DocumentMimeType> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
}

/** Browsers report `type` inconsistently (empty for CSV on some platforms); fall back to the extension. */
export function declaredMimeType(file: File): DocumentMimeType | null {
  const fromBrowser = DOCUMENT_MIME_TYPES.find((m) => m === file.type)
  if (fromBrowser) return fromBrowser
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return BY_EXTENSION[ext] ?? null
}

export function useDocuments(caseId: string) {
  return useQuery({
    queryKey: documentKeys.list(caseId),
    queryFn: () => apiGet<DocumentListResponse>(`/api/v1/cases/${caseId}/documents`),
    // A document may still be scanning when listed; poll until nothing is in flight.
    refetchInterval: (query) =>
      query.state.data?.documents.some((d) => d.availability === 'scanning' || d.availability === 'awaiting_upload') ? 3000 : false,
  })
}

export interface UploadProgress {
  phase: 'authorizing' | 'uploading' | 'verifying'
  /** 0..1 of the PUT body sent; only meaningful while `uploading`. */
  sent: number
}

export interface UploadInput {
  file: File
  document_type: string
  classification: UploadAuthorizationRequest['classification']
  onProgress?: (p: UploadProgress) => void
}

async function sha256Hex(file: File): Promise<string | undefined> {
  if (!('subtle' in crypto)) return undefined
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/** PUT with progress events; `fetch` cannot report upload progress, so this stays on XHR. */
function putWithProgress(url: string, headers: Record<string, string>, file: File, onProgress: (sent: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    // The browser sets forbidden headers (Content-Length) itself; the rest are part of the URL's signature.
    for (const [k, v] of Object.entries(headers)) if (k.toLowerCase() !== 'content-length') xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`storage upload failed with ${xhr.status}`)))
    xhr.onerror = () => reject(new Error('storage upload failed (network)'))
    xhr.send(file)
  })
}

/**
 * authorize → PUT to the presigned URL → complete. The server verifies size, hash and magic bytes
 * and scans the object before it becomes `available`; the returned view may still be `scanning`.
 */
export function useUploadDocument(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, document_type, classification, onProgress }: UploadInput): Promise<DocumentView> => {
      const declared = declaredMimeType(file)
      if (!declared) throw new Error('Only PDF, PNG, JPEG, XLSX and CSV files are accepted')
      if (file.size === 0) throw new Error('The file is empty')
      if (file.size > MAX_DOCUMENT_BYTES) throw new Error('Files must be 25 MB or smaller')

      onProgress?.({ phase: 'authorizing', sent: 0 })
      const body: UploadAuthorizationRequest = {
        document_type,
        classification,
        declared_mime_type: declared,
        original_filename: file.name,
        size_bytes: file.size,
      }
      const sha256 = await sha256Hex(file)
      if (sha256) body.sha256 = sha256
      const authorized = await apiSend<UploadAuthorizationResponse>('POST', `/api/v1/cases/${caseId}/documents/upload-authorizations`, body, {
        [IDEMPOTENCY_HEADER]: crypto.randomUUID(),
      })

      onProgress?.({ phase: 'uploading', sent: 0 })
      await putWithProgress(authorized.upload.url, authorized.upload.headers, file, (sent) => onProgress?.({ phase: 'uploading', sent }))

      onProgress?.({ phase: 'verifying', sent: 1 })
      const done = await apiSend<{ document: DocumentView }>('POST', `/api/v1/cases/${caseId}/documents/${authorized.document.id}/complete`)
      return done.document
    },
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: documentKeys.list(caseId) }), qc.invalidateQueries({ queryKey: lenderKeys.routing(caseId) })]),
  })
}

/** Mints a ~60 s URL; the server refuses anything that is not stored, clean and undeleted. */
export function useAccessDocument(caseId: string) {
  return useMutation({
    mutationFn: (documentId: string) => apiSend<DocumentAccessResponse>('POST', `/api/v1/cases/${caseId}/documents/${documentId}/access`),
  })
}

export function useDeleteDocument(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => apiSend<{ document: DocumentView }>('DELETE', `/api/v1/cases/${caseId}/documents/${documentId}`),
    onSettled: () => Promise.all([qc.invalidateQueries({ queryKey: documentKeys.list(caseId) }), qc.invalidateQueries({ queryKey: lenderKeys.routing(caseId) })]),
  })
}
